import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../lib/firebase';
import { TaskCommentAttachment } from '../types/task';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const ALLOWED_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain'
];

/**
 * Fájl validálása
 */
export const validateFile = (file: File): { valid: boolean; error?: string } => {
  if (file.size > MAX_FILE_SIZE) {
    return {
      valid: false,
      error: `A fájl túl nagy! Maximum ${MAX_FILE_SIZE / 1024 / 1024} MB lehet.`
    };
  }

  if (!ALLOWED_TYPES.includes(file.type)) {
    return {
      valid: false,
      error: 'Nem támogatott fájltípus! Engedélyezett: képek, PDF, Word, Excel, txt'
    };
  }

  return { valid: true };
};

/**
 * Fájl feltöltése Firebase Storage-ba
 */
export const uploadCommentAttachment = async (
  taskId: string,
  commentId: string,
  file: File,
  organizationId: string
): Promise<TaskCommentAttachment> => {
  // Validálás
  const validation = validateFile(file);
  if (!validation.valid) {
    throw new Error(validation.error);
  }

  // Egyedi fájlnév generálása
  const timestamp = Date.now();
  const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
  const fileName = `${timestamp}_${sanitizedName}`;
  const storagePath = `organizations/${organizationId}/tasks/${taskId}/comments/${commentId}/${fileName}`;

  // Feltöltés
  const storageRef = ref(storage, storagePath);
  const metadata = {
    contentType: file.type,
    customMetadata: {
      originalName: file.name,
      taskId,
      commentId,
      organizationId
    }
  };

  await uploadBytes(storageRef, file, metadata);
  const downloadURL = await getDownloadURL(storageRef);

  // Attachment objektum létrehozása
  const attachment: TaskCommentAttachment = {
    id: fileName,
    name: fileName,
    originalName: file.name,
    url: downloadURL,
    type: file.type,
    size: file.size,
    uploadedAt: new Date()
  };

  return attachment;
};

/**
 * Fájl törlése Firebase Storage-ból
 */
export const deleteCommentAttachment = async (
  taskId: string,
  commentId: string,
  attachmentId: string,
  organizationId: string
): Promise<void> => {
  const storagePath = `organizations/${organizationId}/tasks/${taskId}/comments/${commentId}/${attachmentId}`;
  const storageRef = ref(storage, storagePath);
  
  try {
    await deleteObject(storageRef);
  } catch (error: any) {
    // Ha a fájl nem létezik, ne dobjunk hibát
    if (error.code !== 'storage/object-not-found') {
      throw error;
    }
  }
};

/**
 * Fájl méret formázása
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

/**
 * Fájl típus ikon meghatározása
 */
export const getFileIcon = (type: string): string => {
  if (type.startsWith('image/')) return '🖼️';
  if (type === 'application/pdf') return '📄';
  if (type.includes('word')) return '📝';
  if (type.includes('excel') || type.includes('sheet')) return '📊';
  if (type === 'text/plain') return '📃';
  return '📎';
};

/**
 * Kép-e a fájl
 */
export const isImage = (type: string): boolean => {
  return type.startsWith('image/');
};
